import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Switch } from "antd";
import { Link } from "react-router-dom";
import { EditInfo, DeleteModal, PreviewOffer } from ".";
import { getOffersList, patchOfferStatus } from "../../appRedux/actions/offers";

function ListView() {
  const dispatch = useDispatch();
  const offerList = useSelector((state) => state.offers.offerList);
  const queryInfo = useSelector((state) => state.offers.queryInfo);
  const totalRecords = useSelector((state) => state.offers.totalRecords);
  const pages = useSelector((state) => state.offers.pages);
  const [previewOffer, setPreviewOffer] = useState(null);

  useEffect(() => {
    dispatch(getOffersList(queryInfo)).catch(() => {});
  }, []);

  const handleTableChange = (pagination) => {
    const page = pagination?.current || 1;
    const size = pagination?.pageSize || queryInfo.size;
    dispatch(getOffersList({ ...queryInfo, page, size })).catch(() => {});
  };

  const discountLabel = (record) => {
    if (record.discountType === "PERCENT") return `${record.discountValue}% off`;
    return `₹${record.discountValue} off`;
  };

  const columns = [
    {
      title: "Title & Details",
      dataIndex: "title",
      key: "title",
      align: "left",
      render: (title, record) => {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span 
              onClick={() => setPreviewOffer(record)}
              className="offer-title-link"
              style={{ fontWeight: "600", fontSize: "14px", color: "#2f7edc" }}
              title="Click to preview offer"
            >
              {title || "—"}
            </span>
            {record.description && (
              <span style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                {record.description.length > 50 ? `${record.description.substring(0, 50)}...` : record.description}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Target Scope",
      key: "scope",
      align: "center",
      render: (_, record) => {
        const level = record.level;
        const color =
          level === "GLOBAL"
            ? "geekblue"
            : level === "MERCHANT"
            ? "orange"
            : "purple";

        let targetBadge = null;
        if (record.level === "MERCHANT") {
          targetBadge = record.merchant ? (
            <Tag color="cyan" style={{ borderRadius: '6px', padding: '4px 12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px', margin: 0 }}>
              <i className="la la-store" style={{ fontSize: "14px" }} />
              <span>{record.merchant}</span>
            </Tag>
          ) : (
            <span style={{ color: "#aaa" }}>—</span>
          );
        } else if (record.level === "COURSE") {
          targetBadge = record.courseId?.title ? (
            <Tag color="magenta" style={{ borderRadius: '6px', padding: '4px 12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px', margin: 0 }}>
              <i className="la la-graduation-cap" style={{ fontSize: "14px" }} />
              <span style={{ display: "inline-block", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={record.courseId.title}>
                {record.courseId.title}
              </span>
            </Tag>
          ) : (
            <span style={{ color: "#aaa" }}>{record.courseId || "—"}</span>
          );
        }

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "center", padding: "4px 0" }}>
            <Tag color={color} style={{ borderRadius: '6px', padding: '4px 12px', fontWeight: 600, margin: 0 }}>{level}</Tag>
            {targetBadge}
          </div>
        );
      },
    },
    {
      title: "Discount",
      key: "discount",
      align: "center",
      render: (_, record) => {
        const isPercent = record.discountType === "PERCENT";
        return (
          <Tag color="green" style={{ fontSize: '13px', padding: '3px 10px', borderRadius: '6px', fontWeight: 600 }}>
            {isPercent ? `${record.discountValue}%` : `₹${record.discountValue}`} off
          </Tag>
        );
      },
    },
    {
      title: "Promotions Active",
      key: "promotions",
      align: "center",
      render: (_, record) => {
        const promo = record.promotion || {};
        const activePromos = [];
        if (promo.showOnCoursePage) activePromos.push(<Tag key="l1" color="cyan" style={{ borderRadius: '4px', margin: '2px' }}>Patch</Tag>);
        if (promo.showOnHeaderStrip) activePromos.push(<Tag key="l2" color="blue" style={{ borderRadius: '4px', margin: '2px' }}>Strip</Tag>);
        if (promo.showInCarousel) activePromos.push(<Tag key="l3" color="purple" style={{ borderRadius: '4px', margin: '2px' }}>Carousel</Tag>);

        return activePromos.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px' }}>
            {activePromos}
          </div>
        ) : (
          <span style={{ color: "#aaa" }}>—</span>
        );
      },
    },
    {
      title: "Validity",
      key: "validity",
      align: "center",
      render: (_, record) => {
        if (!record.startAt && !record.endAt) {
          return <Tag color="default" style={{ borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 500 }}>Lifetime</Tag>;
        }
        const start = record.startAt ? new Date(record.startAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "—";
        const end = record.endAt ? new Date(record.endAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "—";
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#475569" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }} title="Start Date">
              <i className="la la-calendar-check text-success" style={{ fontSize: "15px" }} />
              <span>{start}</span>
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }} title="End Date">
              <i className="la la-calendar-times text-danger" style={{ fontSize: "15px" }} />
              <span>{end}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      fixed: "right",
      onCell: () => ({ style: { background: "#fff" } }),
      onHeaderCell: () => ({ style: { background: "#fafafa" } }),
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "15px" }}>
          <Switch
            checked={record.status === "ACTIVE"}
            style={{
              backgroundColor: record.status === "ACTIVE" ? "#52c41a" : undefined
            }}
            onChange={(checked) => {
              const newStatus = checked ? "ACTIVE" : "INACTIVE";
              dispatch(patchOfferStatus(record._id, newStatus))
                .then(() => {
                  dispatch(getOffersList(queryInfo)).catch(() => {});
                })
                .catch(() => {});
            }}
          />
          <EditInfo info={record} key="edit_offer" />
          <DeleteModal info={record} />
        </div>
      ),
    },
  ];

  return (
    <div className="table-responsive">
      <Table
        className="platform-hub-table"
        rowKey="_id"
        columns={columns}
        dataSource={offerList}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: queryInfo?.page || 1,
          pageSize: queryInfo?.size || 10,
          total: totalRecords,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "50"],
          showTotal: (total) => `Total ${total} offers`,
        }}
        onChange={handleTableChange}
      />
      {previewOffer && (
        <PreviewOffer
          info={previewOffer}
          visible={!!previewOffer}
          onClose={() => setPreviewOffer(null)}
        />
      )}
    </div>
  );
}

export default ListView;
